import {
  SEARCH_STATE_PARAMS,
  SEARCH_STATE_SEPARATOR,
  SEARCH_STATE_DISABLED_FILTER_PARAMS,
} from '@xing-com/crate-jobs-constants';
import { FILTER_TYPENAME_TO_NAME } from '@xing-com/crate-jobs-domain-search-maps';
import type { JobSearchQueryBodyExtractedFiltersFragment } from '@xing-com/crate-jobs-graphql';

type SearchStateParams = keyof typeof SEARCH_STATE_PARAMS;

type SearchStateDisabledFilterParams =
  (typeof SEARCH_STATE_DISABLED_FILTER_PARAMS)[keyof typeof SEARCH_STATE_DISABLED_FILTER_PARAMS];

type StringParams =
  | Extract<
      SearchStateParams,
      'keywords' | 'location' | 'cityId' | 'radius' | 'salary'
    >
  | typeof SEARCH_STATE_DISABLED_FILTER_PARAMS.salary;

type StringArrayParams = Exclude<
  SearchStateParams | SearchStateDisabledFilterParams,
  StringParams | 'sort' | 'page' | 'sincePeriod' | 'since'
>;

type TemporaryResult = Record<StringArrayParams, string[]> &
  Record<StringParams, string>;

export const buildSearchState = (
  body: JobSearchQueryBodyExtractedFiltersFragment
): Record<string, string> => {
  // @ts-expect-error Properties are initialized on each step
  const result: TemporaryResult = {};

  if (body.keywords) {
    result[SEARCH_STATE_PARAMS.keywords] = body.keywords;
  }

  if (body.location) {
    if (body.location.text) {
      result[SEARCH_STATE_PARAMS.location] = body.location.text;
    }
    if (body.location.city?.name) {
      result[SEARCH_STATE_PARAMS.location] = body.location.city.name;
    }
    if (body.location.city?.id) {
      result[SEARCH_STATE_PARAMS.cityId] = body.location.city.id;
    }
    if (body.location.radius) {
      result[SEARCH_STATE_PARAMS.radius] = `${body.location.radius}`;
    }
  }

  body.filterCollection?.forEach((filter) => {
    if (!filter || !filter.__typename) {
      return;
    }

    if (filter.__typename === 'JobFilterSalary') {
      const { min, max } = filter;
      if (typeof min !== 'number' || typeof max !== 'number') {
        return;
      }
      result[SEARCH_STATE_PARAMS.salary] = `${min}_${max}`;
      return;
    }

    if (!('entityId' in filter)) {
      return;
    }

    const key = FILTER_TYPENAME_TO_NAME[filter.__typename];
    if (result[key]) {
      result[key].push(filter.entityId);
    } else {
      result[key] = [filter.entityId];
    }
  });

  const disabledFilters = body.extractedFilter?.disabled;
  if (disabledFilters) {
    if (disabledFilters.salary) {
      result[SEARCH_STATE_DISABLED_FILTER_PARAMS.salary] = 'true';
    }

    const locationDisabled = disabledFilters.location?.name?.filter(Boolean);
    if (locationDisabled) {
      // @ts-expect-error filtering nulls above
      result[SEARCH_STATE_DISABLED_FILTER_PARAMS.location] = locationDisabled;
    }

    Object.entries(SEARCH_STATE_DISABLED_FILTER_PARAMS).forEach(
      ([propertyName, param]) => {
        if (
          propertyName in disabledFilters &&
          // @ts-expect-error `propertyName in disabledFilters` checked above
          disabledFilters[propertyName]?.id
        ) {
          result[param] =
            // @ts-expect-error `propertyName in disabledFilters` checked above
            disabledFilters[propertyName].id;
        }
      }
    );
  }

  const acc: Record<string, string> = {};
  return Object.entries(result).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: Array.isArray(value) ? value.join(SEARCH_STATE_SEPARATOR) : value,
    }),
    acc
  );
};
